import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  GraduationCap, Plus, Trash2, Save, X, Calendar, Award, 
  FileText, Upload, Download, CheckCircle, XCircle, Edit2,
  ChevronDown, ChevronUp, BookOpen, ClipboardCheck, Building2
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { katLabel } from "@/lib/dokumentKategorien";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Kurskatalog (auch für Selbstzahler/Einrichtung — Kurse ohne AZAV-Maßnahme)
// Match zur Kursliste in App.js -> VERTRIEB_KURSE
const KURS_KATALOG = [
  { id: "alltagsbegleiter_betreuungskraft", name: "Alltagsbegleiter / Betreuungskraft (kleiner Schein)", ue: 40 },
  { id: "rezertifizierung_betreuungskraft", name: "Rezertifizierung Betreuungskraft", ue: 16 },
  { id: "pflegehilfskraft_betreuungsassistent", name: "Pflegehilfskraft & Betreuungsassistent (§§ 43b, 53b, 53c)", ue: 429 },
  { id: "pflegehelfer_qn2", name: "Pflegehilfskraft — Qualifikationsniveau 2 (QN2)", ue: 429 },
  { id: "pflegeassistent_lg1_lg2", name: "Pflegeassistent LG1 & LG2", ue: 208 },
  { id: "pflegeassistent_lg1_lg2_bg", name: "Pflegeassistent LG1 & LG2 (BG)", ue: 208 },
  { id: "lg3_lg4", name: "Weiterqualifikation LG3 & LG4", ue: 120 },
  { id: "ambulanter_pflegeassistent", name: "Ambulanter Pflegeassistent", ue: 300 },
  { id: "berufsspezifisches_deutsch", name: "Berufsspezifisches Deutsch für Pflegehelfer", ue: 156 },
  { id: "gerontopsychiatrie", name: "Gerontopsychiatrie", ue: 420 },
  { id: "inklusionsassistenz", name: "Inklusionsassistenz", ue: 80 },
  { id: "palliativ_care", name: "Palliativ Care", ue: 120 },
  { id: "praxisanleitung", name: "Praxisanleitung", ue: 300 },
  { id: "rezertifizierung_praxisanleiter", name: "Rezertifizierung Praxisanleiter", ue: 24 },
  { id: "zusatzqualifikation_wunden", name: "Zusatzqualifikation chron. Wunden", ue: 84 },
  { id: "wundexperte", name: "Wundexperte", ue: 84 },
  { id: "wundtherapeut", name: "Fachtherapeut Wunde nach HKP-Richtlinien", ue: 120 },
  { id: "pflegetherapeut_wunde", name: "Pflegetherapeut Wunde nach HKP-Richtlinien", ue: 128 },
  { id: "expertenkurs_intensivpflege", name: "Expertenkurs Außerklinische Intensivpflege", ue: 200 },
  { id: "pflegefachexpertin", name: "Pflegefachexpertin", ue: 0 },
];

// Kurs-Anhänge: gleiche Begriffe wie überall (lib/dokumentKategorien)
const KURS_ANHANG_CODES = ["AL", "KB", "PR", "ZE", "PV", "SO"];

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString('de-DE');
  } catch {
    return dateStr;
  }
};

// Einzelner Kurs-Block
function KursBlock({ kurs, teilnehmerId, massnahmen, onUpdate, onDelete }) {
  const [isOpen, setIsOpen] = useState(kurs.status === "aktiv");
  const [isEditing, setIsEditing] = useState(false);
  // Defensiver Init: falls Datum in kurs_start/kurs_ende statt start/ende steht
  const [editForm, setEditForm] = useState({ 
    ...kurs,
    start: kurs.start || kurs.kurs_start || "",
    ende: kurs.ende || kurs.kurs_ende || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      // IMMER den Kursnamen als Identifier verwenden - das ist am zuverlässigsten
      const kursId = encodeURIComponent(kurs.kurs_name);
      console.log(`Speichere Kurs: ${kurs.kurs_name} (identifier: ${kursId})`);
      await axios.put(`${API}/teilnehmer/${teilnehmerId}/kurse/${kursId}`, editForm);
      toast.success("Kurs gespeichert");
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
    setSaving(false);
  };

  const handleFileUpload = async (e, kategorie) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("kategorie", kategorie);

    try {
      // IMMER den Kursnamen als Identifier verwenden
      const kursId = encodeURIComponent(kurs.kurs_name);
      await axios.post(`${API}/teilnehmer/${teilnehmerId}/kurse/${kursId}/anhang`, formData);
      toast.success(`${file.name} hochgeladen`);
      onUpdate();
    } catch (error) {
      console.error("Upload-Fehler:", error);
      toast.error("Fehler beim Hochladen");
    }
    setUploadingFile(false);
    e.target.value = "";
  };

  const handleDownload = async (anhangId, filename) => {
    try {
      // IMMER den Kursnamen als Identifier verwenden
      const kursId = encodeURIComponent(kurs.kurs_name);
      const response = await axios.get(
        `${API}/teilnehmer/${teilnehmerId}/kurse/${kursId}/anhang/${anhangId}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Fehler beim Download");
    }
  };

  const statusColor = {
    aktiv: "bg-green-100 text-green-700",
    abgeschlossen: "bg-blue-100 text-blue-700",
    abgebrochen: "bg-red-100 text-red-700"
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className={`mb-3 ${kurs.status === "abgeschlossen" ? "border-blue-200" : kurs.status === "abgebrochen" ? "border-red-200" : "border-green-200"}`}>
        <CollapsibleTrigger asChild>
          <CardHeader className="py-3 px-4 cursor-pointer hover:bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <GraduationCap size={20} className={kurs.status === "abgeschlossen" || kurs.status === "abgebrochen" ? "text-gray-400" : "text-green-600"} />
                <div>
                  <h4 className="font-medium text-sm">{kurs.kurs_name}</h4>
                  <p className="text-xs text-gray-500">
                    {formatDate(kurs.start || kurs.kurs_start)} - {formatDate(kurs.ende || kurs.kurs_ende)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={statusColor[kurs.status] || statusColor.aktiv}>
                  {kurs.status === "abgeschlossen" ? "Abgeschlossen" : kurs.status === "abgebrochen" ? "Abgebrochen" : "Aktiv"}
                </Badge>
                {kurs.bestanden && (
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle size={12} className="mr-1" /> Bestanden
                  </Badge>
                )}
                {kurs.zertifikatsnummer && (
                  <Badge className="bg-purple-100 text-purple-700">
                    <Award size={12} className="mr-1" /> Zertifikat
                  </Badge>
                )}
                {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0 pb-4 px-4">
            {isEditing ? (
              /* Bearbeitungsmodus */
              <div className="space-y-4">
                {/* Zeitraum */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Kursstart</Label>
                    <Input
                      type="date"
                      value={editForm.start || ""}
                      onChange={(e) => setEditForm({ ...editForm, start: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Kursende</Label>
                    <Input
                      type="date"
                      value={editForm.ende || ""}
                      onChange={(e) => setEditForm({ ...editForm, ende: e.target.value })}
                    />
                  </div>
                </div>

                {/* Prüfung */}
                <div className="border-t pt-3" data-testid="kurs-pruefung-hinweis">
                  <h5 className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                    <ClipboardCheck size={14} /> Prüfung
                  </h5>
                  <p className="text-xs text-gray-600">
                    Prüfungen werden nur noch auf der Prüfungen-Seite eingetragen und hier automatisch übernommen.{" "}
                    <a href={`/pruefungen?tn=${teilnehmerId}`} className="font-semibold text-[#D9775A] underline" data-testid="link-pruefungsseite">Zur Prüfung</a>
                  </p>
                </div>

                {/* Zertifikat */}
                <div className="border-t pt-3">
                  <h5 className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                    <Award size={14} /> Zertifikat
                  </h5>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Zertifikatsnummer</Label>
                      <Input
                        placeholder="z.B. 2026-PDL-001"
                        value={editForm.zertifikatsnummer || ""}
                        onChange={(e) => setEditForm({ ...editForm, zertifikatsnummer: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Ausgestellt am</Label>
                      <Input
                        type="date"
                        value={editForm.zertifikat_ausgestellt_am || ""}
                        onChange={(e) => setEditForm({ ...editForm, zertifikat_ausgestellt_am: e.target.value })}
                        data-testid="input-zertifikat-ausgestellt-am"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Versendet am</Label>
                      <Input
                        type="date"
                        value={editForm.zertifikat_versendet_am || ""}
                        onChange={(e) => setEditForm({ ...editForm, zertifikat_versendet_am: e.target.value })}
                        data-testid="input-zertifikat-versendet-am"
                      />
                    </div>
                  </div>
                </div>

                {/* Praktikum (falls erforderlich) */}
                <div className="border-t pt-3">
                  <h5 className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                    <Building2 size={14} /> Praktikum
                  </h5>
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox
                      checked={editForm.praktikum_erforderlich || false}
                      onCheckedChange={(checked) => setEditForm({ ...editForm, praktikum_erforderlich: checked })}
                    />
                    <Label className="text-xs">Praktikum erforderlich</Label>
                  </div>
                  {editForm.praktikum_erforderlich && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Einrichtung</Label>
                          <Input
                            placeholder="Name der Einrichtung"
                            value={editForm.praktikumseinrichtung || ""}
                            onChange={(e) => setEditForm({ ...editForm, praktikumseinrichtung: e.target.value })}
                          />
                        </div>
                        <div className="flex items-center gap-2 pt-5">
                          <Checkbox
                            checked={editForm.praktikumsvertrag_vorhanden || false}
                            onCheckedChange={(checked) => setEditForm({ ...editForm, praktikumsvertrag_vorhanden: checked })}
                          />
                          <Label className="text-xs">Vertrag vorhanden</Label>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div>
                          <Label className="text-xs">Beginn</Label>
                          <Input
                            type="date"
                            value={editForm.praktikum_beginn || ""}
                            onChange={(e) => setEditForm({ ...editForm, praktikum_beginn: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Ende</Label>
                          <Input
                            type="date"
                            value={editForm.praktikum_ende || ""}
                            onChange={(e) => setEditForm({ ...editForm, praktikum_ende: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Checkbox
                          checked={editForm.praktikum_nachweis_erbracht || false}
                          onCheckedChange={(checked) => setEditForm({ ...editForm, praktikum_nachweis_erbracht: checked })}
                        />
                        <Label className="text-xs">Praktikumsnachweis erbracht</Label>
                      </div>
                    </>
                  )}
                </div>

                {/* Status */}
                <div className="border-t pt-3">
                  <h5 className="text-xs font-medium text-gray-600 mb-2">Status</h5>
                  <Select
                    value={editForm.status || "aktiv"}
                    onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aktiv">Aktiv</SelectItem>
                      <SelectItem value="abgeschlossen">Abgeschlossen</SelectItem>
                      <SelectItem value="abgebrochen">Abgebrochen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
                    <Save size={14} className="mr-1" /> Speichern
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setIsEditing(false); setEditForm({ ...kurs }); }}>
                    <X size={14} className="mr-1" /> Abbrechen
                  </Button>
                </div>
              </div>
            ) : (
              /* Ansichtsmodus */
              <div className="space-y-3">
                {/* Prüfungsdaten */}
                {(kurs.pruefung_am || kurs.punkte_schriftlich || kurs.bestanden) && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h5 className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                      <ClipboardCheck size={14} /> Prüfung
                    </h5>
                    <div className="grid grid-cols-4 gap-2 text-sm">
                      {kurs.pruefung_am && (
                        <div>
                          <span className="text-xs text-gray-500">Termin</span>
                          <p className="font-medium">{formatDate(kurs.pruefung_am)}</p>
                        </div>
                      )}
                      {kurs.punkte_schriftlich != null && (
                        <div>
                          <span className="text-xs text-gray-500">Schriftlich</span>
                          <p className="font-medium">{kurs.punkte_schriftlich} Pkt</p>
                        </div>
                      )}
                      {kurs.punkte_muendlich != null && (
                        <div>
                          <span className="text-xs text-gray-500">Mündlich</span>
                          <p className="font-medium">{kurs.punkte_muendlich} Pkt</p>
                        </div>
                      )}
                      {kurs.gesamtpunkte != null && (
                        <div>
                          <span className="text-xs text-gray-500">Gesamt</span>
                          <p className="font-medium">{kurs.gesamtpunkte} Pkt</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Zertifikat */}
                {kurs.zertifikatsnummer && (
                  <div className="bg-purple-50 rounded-lg p-3">
                    <h5 className="text-xs font-medium text-purple-700 mb-1 flex items-center gap-1">
                      <Award size={14} /> Zertifikat
                    </h5>
                    <p className="font-medium text-sm">{kurs.zertifikatsnummer}</p>
                    {kurs.zertifikat_ausgestellt_am && (
                      <p className="text-xs text-gray-500">Ausgestellt: {formatDate(kurs.zertifikat_ausgestellt_am)}</p>
                    )}
                    {kurs.zertifikat_versendet_am && (
                      <p className="text-xs text-gray-500" data-testid="text-zertifikat-versendet-am">Versendet: {formatDate(kurs.zertifikat_versendet_am)}</p>
                    )}
                  </div>
                )}

                {/* Praktikum */}
                {kurs.praktikum_erforderlich && (
                  <div className="bg-indigo-50 rounded-lg p-3">
                    <h5 className="text-xs font-medium text-indigo-700 mb-1 flex items-center gap-1">
                      <Building2 size={14} /> Praktikum
                    </h5>
                    {kurs.praktikumseinrichtung && (
                      <p className="font-medium text-sm">{kurs.praktikumseinrichtung}</p>
                    )}
                    <div className="flex gap-4 text-xs mt-1">
                      {kurs.praktikum_beginn && <span>Von: {formatDate(kurs.praktikum_beginn)}</span>}
                      {kurs.praktikum_ende && <span>Bis: {formatDate(kurs.praktikum_ende)}</span>}
                    </div>
                    <div className="flex gap-3 mt-2">
                      {kurs.praktikumsvertrag_vorhanden && (
                        <Badge variant="outline" className="text-xs">✓ Vertrag</Badge>
                      )}
                      {kurs.praktikum_nachweis_erbracht && (
                        <Badge variant="outline" className="text-xs">✓ Nachweis</Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Anhänge */}
                <div className="border-t pt-3">
                  <h5 className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                    <FileText size={14} /> Anhänge ({kurs.anhaenge?.length || 0})
                  </h5>
                  {kurs.anhaenge?.length > 0 ? (
                    <div className="space-y-1">
                      {kurs.anhaenge.map((a, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-50 rounded p-2">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-stone-100 text-stone-700">
                              {katLabel(a.kategorie)}
                            </Badge>
                            <span className="text-sm">{a.filename}</span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownload(a.id, a.filename)}
                          >
                            <Download size={14} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">Keine Anhänge</p>
                  )}
                  
                  {/* Upload */}
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {KURS_ANHANG_CODES.map((key) => (
                      <label key={key} className="cursor-pointer" data-testid={`kurs-upload-${key}`}>
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, key)}
                          disabled={uploadingFile}
                        />
                        <Badge variant="outline" className="hover:bg-gray-100 cursor-pointer text-xs">
                          <Upload size={10} className="mr-1" /> {katLabel(key)}
                        </Badge>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Aktionen */}
                <div className="flex gap-2 pt-2 border-t">
                  <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                    <Edit2 size={14} className="mr-1" /> Bearbeiten
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-500 hover:text-red-700"
                    onClick={() => {
                      if (confirm(`Kurs "${kurs.kurs_name}" wirklich löschen?`)) {
                        // IMMER den Kursnamen verwenden - das ist am zuverlässigsten
                        // da IDs zwischen Preview und Produktion unterschiedlich sein können
                        const kursIdentifier = encodeURIComponent(kurs.kurs_name);
                        console.log(`Lösche Kurs: ${kurs.kurs_name} (identifier: ${kursIdentifier})`);
                        onDelete(kursIdentifier);
                      }
                    }}
                  >
                    <Trash2 size={14} className="mr-1" /> Löschen
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Hauptkomponente
export default function TeilnehmerKurse({ teilnehmerId, teilnehmer, massnahmen, onKurseChanged }) {
  const [kurse, setKurse] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddKurs, setShowAddKurs] = useState(false);
  const [neuerKurs, setNeuerKurs] = useState({ kurs_id: "", kurs_name: "", start: "", ende: "", massnahme_id: "" });

  const loadKurse = async () => {
    if (!teilnehmerId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/teilnehmer/${teilnehmerId}/kurse`);
      setKurse(response.data.kurse || []);
    } catch (error) {
      console.error("Fehler beim Laden der Kurse:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadKurse();
  }, [teilnehmerId]);

  const nachKursAenderung = () => {
    loadKurse();
    if (onKurseChanged) onKurseChanged();
  };

  const handleAddKurs = async () => {
    if (!neuerKurs.kurs_name) {
      toast.error("Bitte Kurs auswählen");
      return;
    }
    try {
      await axios.post(`${API}/teilnehmer/${teilnehmerId}/kurse`, neuerKurs);
      toast.success("Kurs hinzugefügt");
      setShowAddKurs(false);
      setNeuerKurs({ kurs_id: "", kurs_name: "", start: "", ende: "", massnahme_id: "" });
      loadKurse();
      if (onKurseChanged) onKurseChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen");
    }
  };

  const handleDeleteKurs = async (kursId) => {
    try {
      // kursId kann eine UUID oder ein URL-encodierter Kursname sein
      await axios.delete(`${API}/teilnehmer/${teilnehmerId}/kurse/${kursId}`);
      toast.success("Kurs entfernt");
      loadKurse();
      if (onKurseChanged) onKurseChanged();
    } catch (error) {
      console.error("Fehler beim Löschen:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  // Kurse nach Status gruppieren - berücksichtige fehlenden Status als "aktiv"
  const aktiverKurs = kurse.find(k => k.status === "aktiv" || (!k.status && k.status !== "abgeschlossen" && k.status !== "abgebrochen"));
  const abgeschlosseneKurse = kurse.filter(k => k.status === "abgeschlossen");

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium flex items-center gap-2">
          <GraduationCap size={18} className="text-gray-500" />
          Kurse ({kurse.length})
        </h3>
        {!showAddKurs && (
          <Button size="sm" variant="outline" onClick={() => setShowAddKurs(true)} data-testid="add-kurs-btn">
            <Plus size={14} className="mr-1" /> Kurs hinzufügen
          </Button>
        )}
      </div>

      {/* Neuer Kurs Formular */}
      {showAddKurs && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 space-y-3">
            <h4 className="font-medium text-sm">Neuen Kurs hinzufügen</h4>
            <Select
              value={neuerKurs.massnahme_id || neuerKurs.kurs_id || ""}
              onValueChange={(val) => {
                // Erst in massnahmen suchen, dann im Kurskatalog
                const m = massnahmen.find(m => m.id === val);
                const k = !m ? KURS_KATALOG.find(k => k.id === val) : null;
                setNeuerKurs({
                  ...neuerKurs,
                  massnahme_id: m ? val : "",           // nur wenn echte Maßnahme
                  kurs_id: val,
                  kurs_name: m?.name || k?.name || val,
                  start: m?.start_datum || neuerKurs.start,
                  ende: m?.end_datum || neuerKurs.ende
                });
              }}
            >
              <SelectTrigger data-testid="new-kurs-select-trigger">
                <SelectValue placeholder="Kurs / Maßnahme auswählen" />
              </SelectTrigger>
              <SelectContent>
                {massnahmen.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-stone-500 font-semibold">AZAV-Maßnahmen</div>
                    {massnahmen.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </>
                )}
                <div className="px-2 py-1 mt-1 text-[10px] uppercase tracking-wider text-stone-500 font-semibold border-t pt-2">
                  Kurse (Selbstzahler / Einrichtung)
                </div>
                {KURS_KATALOG
                  // dedup: Katalog-Kurse deren ID bereits als Maßnahme existiert nicht doppelt zeigen
                  .filter(k => !massnahmen.some(m => m.id === k.id))
                  .map(k => (
                    <SelectItem key={k.id} value={k.id} data-testid={`kurs-katalog-${k.id}`}>
                      {k.name}{k.ue ? ` · ${k.ue} UE` : ""}
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Startdatum</Label>
                <Input
                  type="date"
                  value={neuerKurs.start || ""}
                  onChange={(e) => setNeuerKurs({ ...neuerKurs, start: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Enddatum</Label>
                <Input
                  type="date"
                  value={neuerKurs.ende || ""}
                  onChange={(e) => setNeuerKurs({ ...neuerKurs, ende: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddKurs} className="bg-green-600 hover:bg-green-700" data-testid="confirm-add-kurs-btn">
                <Plus size={14} className="mr-1" /> Hinzufügen
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setShowAddKurs(false); setNeuerKurs({ kurs_id: "", kurs_name: "", start: "", ende: "", massnahme_id: "" }); }}>
                Abbrechen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Keine Kurse Hinweis */}
      {kurse.length === 0 && !loading && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <GraduationCap size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500 text-sm">Noch keine Kurse zugeordnet</p>
          <p className="text-gray-400 text-xs">Klicken Sie auf "Kurs hinzufügen" um zu beginnen</p>
        </div>
      )}

      {/* Aktiver Kurs - erster Kurs der aktiv ist oder keinen Status hat */}
      {aktiverKurs && (
        <div>
          <p className="text-xs text-gray-500 mb-1 font-medium">AKTUELLER KURS</p>
          <KursBlock
            key={`${aktiverKurs.id || aktiverKurs.kurs_name}-${aktiverKurs.start || aktiverKurs.kurs_start}-${aktiverKurs.ende || aktiverKurs.kurs_ende}`}
            kurs={aktiverKurs}
            teilnehmerId={teilnehmerId}
            massnahmen={massnahmen}
            onUpdate={nachKursAenderung}
            onDelete={handleDeleteKurs}
          />
        </div>
      )}

      {/* Abgeschlossene Kurse */}
      {abgeschlosseneKurse.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-1 font-medium">ABGESCHLOSSENE KURSE ({abgeschlosseneKurse.length})</p>
          {abgeschlosseneKurse.map((k, idx) => (
            <KursBlock
              key={k.id || idx}
              kurs={k}
              teilnehmerId={teilnehmerId}
              massnahmen={massnahmen}
              onUpdate={nachKursAenderung}
              onDelete={handleDeleteKurs}
            />
          ))}
        </div>
      )}

      {/* Weitere aktive Kurse (Kurse ohne "abgeschlossen" oder "abgebrochen" Status, die nicht der aktiverKurs sind) */}
      {kurse.filter(k => k.status !== "abgeschlossen" && k.status !== "abgebrochen" && k !== aktiverKurs).length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-1 font-medium">WEITERE AKTIVE KURSE</p>
          {kurse.filter(k => k.status !== "abgeschlossen" && k.status !== "abgebrochen" && k !== aktiverKurs).map((k, idx) => (
            <KursBlock
              key={k.id || idx}
              kurs={{...k, status: k.status || "aktiv"}}
              teilnehmerId={teilnehmerId}
              massnahmen={massnahmen}
              onUpdate={nachKursAenderung}
              onDelete={handleDeleteKurs}
            />
          ))}
        </div>
      )}
    </div>
  );
}
